import React, { memo, useCallback } from 'react';
import { components } from 'react-select';
import { Chip, createUseStyles, Popover, Spacer, Text } from '@Sage/hoi-poi-ui';

import { buildMultiSelectChipText } from './multiSelectCellUtils';

import styles from './multiSelectSummaryStyles';

const useStyles = createUseStyles(styles, 'MultiSelectValuesPopover');

export const MULTI_SELECT_POPOVER_TRIGGER = ['hover', 'click'];

export const MultiSelectPopoverContent = memo(({ fieldLabel, labels, classes }) => (
    <div className={classes.popoverContent}>
        {fieldLabel ? (
            <Text type="subtitle" medium>
                {fieldLabel}
            </Text>
        ) : null}
        {fieldLabel && labels?.length ? <Spacer y={2} /> : null}
        {(labels || []).map((label) => (
            <Text key={label} className={classes.popoverItem}>
                {label}
            </Text>
        ))}
    </div>
));

MultiSelectPopoverContent.displayName = 'MultiSelectPopoverContent';

export const MultiSelectSummaryPopover = memo(({ fieldLabel, labels, children }) => {
    const classes = useStyles();

    return (
        <Popover
            content={
                <MultiSelectPopoverContent
                    fieldLabel={fieldLabel}
                    labels={labels}
                    classes={classes}
                />
            }
            placement="bottomLeft"
            trigger={MULTI_SELECT_POPOVER_TRIGGER}
        >
            {children}
        </Popover>
    );
});

MultiSelectSummaryPopover.displayName = 'MultiSelectSummaryPopover';

export const MultiSelectSummaryChip = memo(
    ({ fieldLabel, labels, isReadOnly, onRemove, className }) => {
        const classes = useStyles();
        const chipText = buildMultiSelectChipText(labels);

        const handleRemove = useCallback(
            (event) => {
                event?.preventDefault?.();
                event?.stopPropagation?.();
                onRemove?.(event);
            },
            [onRemove],
        );

        if (!chipText) {
            return null;
        }

        const chip = (
            <Chip
                className={[classes.summaryChip, className].filter(Boolean).join(' ')}
                isFilled
                isReadOnly={isReadOnly}
                onRemove={!isReadOnly && onRemove ? handleRemove : undefined}
                overrides={{
                    wrapper: { className: classes.summaryChipWrapper },
                    Text: { className: classes.summaryChipText },
                    icons: { className: classes.summaryChipIcons },
                }}
            >
                {chipText}
            </Chip>
        );

        if (!labels?.length || labels.length <= 1) {
            return chip;
        }

        return (
            <MultiSelectSummaryPopover fieldLabel={fieldLabel} labels={labels}>
                {chip}
            </MultiSelectSummaryPopover>
        );
    },
);

MultiSelectSummaryChip.displayName = 'MultiSelectSummaryChip';

/** Collapses multiselect values to one summary chip + popover inside react-select. */
export const createMultiSelectValueContainer = ({ fieldLabel, isSelectLocked }) => {
    const Container = memo(({ children, selectProps, ...props }) => {
        const { value, onChange } = selectProps || {};
        const selectedOptions = Array.isArray(value) ? value : [];
        const labels = selectedOptions.map((option) => option?.label).filter(Boolean);
        const [values, input] = React.Children.toArray(children);

        const handleClear = useCallback(() => {
            onChange?.(null, { action: 'clear' });
        }, [onChange]);

        if (!selectedOptions.length) {
            return (
                <components.ValueContainer {...props} selectProps={selectProps}>
                    {values}
                    {input}
                </components.ValueContainer>
            );
        }

        return (
            <components.ValueContainer {...props} selectProps={selectProps}>
                <MultiSelectSummaryChip
                    fieldLabel={fieldLabel}
                    labels={labels}
                    isReadOnly={isSelectLocked}
                    onRemove={handleClear}
                />
                {input}
            </components.ValueContainer>
        );
    });

    Container.displayName = 'MultiSelectValueContainer';
    return Container;
};
